<template>
    <Spinner v-if="displaySpinner" />
    <div class="title-and-controls">
        <EditableTitle v-model="process" icon="pi pi-share-alt" />
        <div class="controls">
            <Button
                v-if="description && !description.content && isEditing"
                v-tooltip.bottom="{
                    value: 'Will generate a first draft of the description based on the context.',
                    showDelay: 100,
                }"
                class="p-button-secondary"
                icon="pi pi-sparkles"
                label="Generate first draft"
                @click="generateProcessDescription"
            />
        </div>
    </div>
    <section class="description" :class="{ 'edit': isEditing }">
        <TextAppArtefact
            v-if="process?.identity && description"
            v-model="description"
            :isEditing="isEditing"
        />
    </section>
    <section class="process-definition" :class="{ 'edit': isEditing }">
        <div
            v-if="isEditing && definition?.content"
            class="process-definition-overlay"
        >
            To edit the process definition,
            <Button
                v-if="organization && stek && process"
                severity="secondary"
                class="maximize-button"
                icon="pi pi-arrow-up-right-and-arrow-down-left-from-center"
                label="Maximize"
                size="small"
                as="RouterLink"
                :to="{
                    name: 'processDefinitionPage',
                    params: {
                        orgName: organization.name,
                        stekName: stek.name,
                        processId: process.identity,
                    },
                }"
            />
            it first.
        </div>
        <template v-if="process">
            <ProcessDefinition
                v-if="definition?.content"
                v-model:bpmnArtefact="definition"
                v-model:highlightElementId="highlightElementId"
                :isNavigationEnabled="true"
                :isScrollingEnabled="false"
                :isMaximizingEnabled="!isEditing"
                :isDisabled="isEditing"
                :isZoomOnHoverEnabled="false"
                class="main-process-definition"
            />
            <ProcessDefinitionCreator
                v-else-if="definition"
                v-model="definition"
                class="main-process-definition"
            />
        </template>
    </section>
    <Panel class="section" :class="{ 'edit': isEditing }" toggleable collapsed>
        <template #header>
            <div class="title-and-controls">
                <h2>Process steps</h2>
            </div>
        </template>
        <ProcessElementTable
            v-if="process"
            v-model="highlightElementId"
            :processElements="processElements"
        />
        <ProcessElementTable
            class="archived-data-table"
            v-if="
                process &&
                archivedProcessElements &&
                archivedProcessElements.length > 0
            "
            v-model="highlightArchivedElementId"
            :processElements="archivedProcessElements"
            header="Archived"
        />
    </Panel>
    <section :class="{ 'edit': isEditing }">
        <Documents
            v-if="process"
            :isEditing="isEditing"
            entityType="process"
            :entity="process"
            :artefacts="artefacts"
        />
    </section>
</template>

<script setup lang="ts">
import Documents from "@/components/Documents.vue";
import EditableTitle from "@/components/EditableTitle.vue";
import ProcessDefinition from "@/components/Process/Definition.vue";
import ProcessDefinitionCreator from "@/components/Process/DefinitionCreator.vue";
import ProcessElementTable from "@/components/ProcessElementTable.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import { useEditManager } from "@/composables/edit-manager";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useProcessManager } from "@/composables/process-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useArtefactsManager } from "@/lib/artefacts/user";
import Spinner from "@/shared/Spinner.vue";
import { EntityType } from "@/stores/entity-store";
import { useProcessElementsManager } from "@/stores/process-elements-store";
import { useTaskManager } from "@/stores/task-store";
import { computed, ref } from "vue";

useTaskManager();
const { organization } = useOrganizationManager();
const { stek } = useStekManager();
const {
    process,
    isFetching: isFetchingProcess,
    definition,
    description,
} = useProcessManager();
const { isEditing } = useEditManager();
const { artefacts } = useArtefactsManager();
const {
    processElements,
    archivedProcessElements,
    isFetching: isFetchingProcessElements,
} = useProcessElementsManager();
const { generateContent } = useGenerateDescriptionDialog();
const isLoading = ref(false);

const displaySpinner = computed(() => {
    return Boolean(
        isFetchingProcessElements.value ||
            isFetchingProcess.value ||
            isLoading.value
    );
});

const highlightElementId = ref<string | null>();
const highlightArchivedElementId = ref<string | null>();

const generateProcessDescription = async () => {
    if (!process.value) {
        return;
    }
    await generateContent(
        process.value.identity,
        EntityType.PROCESS,
        description.value?.updateContent.bind(description.value)
    );
};
</script>

<style scoped lang="scss">
.process-definition {
    height: 600px;
    max-height: 75vh;
    display: flex;
}

.process-definition-overlay {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    padding: 0.5rem 1rem;
    transform: translateY(-50%);
    z-index: 2;
    text-align: center;
    display: inline-block;
}

.controls {
    text-wrap-mode: nowrap;
}

section,
.section {
    border: 1px solid transparent;
    position: relative;

    &:not(.process-definition) {
        padding: 1rem;
    }

    &.description {
        padding-top: 0;
        z-index: 3;
    }

    &.edit {
        border-color: var(--stekz-outline);
        border-radius: 0.5rem;

        &.description {
            padding: 0;
        }
    }
}

.archived-data-table {
    margin-top: 1rem;
}
</style>
